import type {
  FrontendAccessPolicy,
  PlatformEntryContext,
  User,
  UserRole,
} from '@/types';
import { KPI_ANALYTICS_ROUTE, KPI_OPERATOR_PLUS_ROLES } from './kpiAdminAccess';
import { isPublicPlatformEntryHostname } from '@/utils/publicEntryHost';

export const ROLE_ACCESS_CANONICAL_ORDER: UserRole[] = [
  'super_admin',
  'developer',
  'admin',
  'chief',
  'senior',
  'operator',
  'department_head',
  'department_employee',
  'analyst',
  'observer',
];

export const DEVELOPER_WORKBENCH_ROLES = new Set<UserRole>([
  'developer',
  'admin',
  'super_admin',
]);

export const KPI_ANALYTICS_ROLES = new Set<UserRole>(KPI_OPERATOR_PLUS_ROLES);

export const HELPDESK_SYSTEM_TASKS_CONTROL_ROLES = new Set<UserRole>([
  'super_admin',
  'developer',
  'admin',
  'chief',
]);

const TENANT_SETTINGS_ROLES = new Set<UserRole>([
  'operator',
  'department_head',
  'senior',
  'chief',
  'admin',
  'developer',
  'super_admin',
]);

const ROLES_PAGE_ROLES = new Set<UserRole>([
  'senior',
  'chief',
  'admin',
  'developer',
  'super_admin',
]);

const TELEGRAM_ROLES_ROLES = new Set<UserRole>([
  'senior',
  'chief',
  'admin',
  'developer',
  'super_admin',
]);

const MENU_VISIBILITY_ROLES = new Set<UserRole>([
  'admin',
  'developer',
  'super_admin',
]);

const ADMIN_SURFACE_ROLES = new Set<UserRole>([
  'admin',
  'developer',
  'super_admin',
]);

export const CITY_REPORT_CENTER_ROLES = new Set<UserRole>([
  'operator',
  'senior',
  'chief',
  'admin',
  'developer',
  'super_admin',
]);

const MAX_PERSONAL_REPLY_ROLES = new Set<UserRole>([
  'super_admin',
  'developer',
  'admin',
  'chief',
  'senior',
]);

const OBSERVER_WHITELIST_MENU_KEYS = new Set([
  '/messages',
  '/tickets',
  '/map',
]);

function policyOrFallback(
  accessPolicy: FrontendAccessPolicy | null | undefined,
  key: keyof FrontendAccessPolicy,
  fallback: boolean,
): boolean {
  const value = accessPolicy?.[key];
  return typeof value === 'boolean' ? value : fallback;
}

export function normalizeFrontendRole(role: string | null | undefined): UserRole | null {
  const normalized = String(role || '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'developer_admin') return 'developer';
  if ((ROLE_ACCESS_CANONICAL_ORDER as string[]).includes(normalized)) {
    return normalized as UserRole;
  }
  return null;
}

export function isObserverWhitelistRole(role: string | null | undefined): boolean {
  return normalizeFrontendRole(role) === 'observer';
}

export function isAnalystRole(role: string | null | undefined): boolean {
  return normalizeFrontendRole(role) === 'analyst';
}

export function buildFallbackFrontendAccessPolicy(role: string | null | undefined): FrontendAccessPolicy {
  const normalized = normalizeFrontendRole(role);
  return {
    role: normalized ?? '',
    capability_profile: normalized === 'admin' ? 'developer' : normalized ?? '',
    role_rank: normalized ? ROLE_ACCESS_CANONICAL_ORDER.length - ROLE_ACCESS_CANONICAL_ORDER.indexOf(normalized) : null,
    operator_above_department_head: true,
    can_access_helpdesk_support: normalized ? DEVELOPER_WORKBENCH_ROLES.has(normalized) : false,
    can_access_helpdesk_engineering: normalized ? DEVELOPER_WORKBENCH_ROLES.has(normalized) : false,
    can_access_helpdesk_system_tasks: normalized ? HELPDESK_SYSTEM_TASKS_CONTROL_ROLES.has(normalized) : false,
    can_reply_in_chat: normalized ? ['operator', 'senior', 'chief', 'admin', 'developer', 'super_admin'].includes(normalized) : false,
    can_access_settings_surface: normalized ? TENANT_SETTINGS_ROLES.has(normalized) : false,
    can_access_tenant_settings_page: normalized ? TENANT_SETTINGS_ROLES.has(normalized) : false,
    has_full_ticket_visibility: normalized ? ['operator', 'senior', 'chief', 'admin', 'developer', 'super_admin'].includes(normalized) : false,
    can_manage_tenant_users: normalized ? ROLES_PAGE_ROLES.has(normalized) : false,
    can_reset_user_passwords: normalized ? ['chief', 'admin', 'developer', 'super_admin'].includes(normalized) : false,
    can_access_admin_surface: normalized ? ADMIN_SURFACE_ROLES.has(normalized) : false,
    can_access_developer_workbench: normalized ? DEVELOPER_WORKBENCH_ROLES.has(normalized) : false,
    can_access_roles_page: normalized ? ROLES_PAGE_ROLES.has(normalized) : false,
    can_access_users_role_surface: normalized ? ROLES_PAGE_ROLES.has(normalized) : false,
    can_access_telegram_roles_surface: normalized ? TELEGRAM_ROLES_ROLES.has(normalized) : false,
    can_manage_role_menu_visibility: normalized ? MENU_VISIBILITY_ROLES.has(normalized) : false,
    can_access_message_feed_advanced_bulk_edit: normalized ? ['admin', 'developer'].includes(normalized) : false,
    can_access_message_feed_pipe_cases_filter: normalized ? ['developer', 'admin', 'chief', 'super_admin'].includes(normalized) : false,
    can_access_message_feed_cleanup: normalized ? ['developer', 'admin', 'chief', 'super_admin'].includes(normalized) : false,
    can_access_message_feed_moderation_labels: normalized ? ['developer', 'admin', 'chief', 'super_admin'].includes(normalized) : false,
    can_access_kpi_analytics: normalized ? KPI_ANALYTICS_ROLES.has(normalized) : false,
    allowed_target_roles: [],
    observer_whitelist_sections: normalized === 'observer' ? ['messages', 'tickets', 'map'] : [],
  };
}

export function buildFailClosedFrontendAccessPolicy(user: Pick<User, 'role' | 'access_mode' | 'access_status'>): FrontendAccessPolicy {
  const fallback = buildFallbackFrontendAccessPolicy(user.role);
  if (user.access_mode !== 'explicit') return fallback;
  return {
    ...fallback,
    capability_profile: 'explicit-fail-closed',
    operator_above_department_head: false,
    can_access_helpdesk_support: false,
    can_access_helpdesk_engineering: false,
    can_access_helpdesk_system_tasks: false,
    can_reply_in_chat: false,
    can_access_settings_surface: false,
    can_access_tenant_settings_page: false,
    has_full_ticket_visibility: false,
    can_manage_tenant_users: false,
    can_reset_user_passwords: false,
    can_access_admin_surface: false,
    can_access_developer_workbench: false,
    can_access_roles_page: false,
    can_access_users_role_surface: false,
    can_access_telegram_roles_surface: false,
    can_manage_role_menu_visibility: false,
    can_access_message_feed_advanced_bulk_edit: false,
    can_access_message_feed_pipe_cases_filter: false,
    can_access_message_feed_cleanup: false,
    can_access_message_feed_moderation_labels: false,
    can_access_kpi_analytics: false,
    allowed_target_roles: [],
    observer_whitelist_sections: [],
    access_mode: 'explicit',
    account_access_status: user.access_status ?? 'pending',
    tenant_scope_ids: [],
    scope_summary: {},
    actions: [],
  };
}

export function canAccessKpiAnalytics(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  if (accessPolicy?.access_mode === 'explicit') {
    return Boolean(accessPolicy.actions?.includes('analytics.read'));
  }
  const normalized = normalizeFrontendRole(role);
  if (!normalized || !KPI_ANALYTICS_ROLES.has(normalized)) return false;
  return policyOrFallback(
    accessPolicy,
    'can_access_kpi_analytics',
    true,
  );
}

export function canAccessCityReportCenter(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  if (accessPolicy?.access_mode === 'explicit') return false;
  const normalized = normalizeFrontendRole(role);
  if (!normalized || !CITY_REPORT_CENTER_ROLES.has(normalized)) return false;
  return policyOrFallback(
    accessPolicy,
    'has_full_ticket_visibility',
    true,
  );
}

const EXPLICIT_WORKING_ROUTE_ACTIONS: Readonly<Record<string, string>> = {
  '/messages': 'messages.read',
  '/tickets': 'tickets.read',
  '/map': 'map.read',
  '/roles': 'users.manage_access',
  '/tenant-settings': 'governance.read',
  '/tenant-communications': 'settings.manage',
  '/new-analytics': 'analytics.read',
};

const EXPLICIT_WORKING_ROUTE_PRIORITY = [
  '/tickets',
  '/messages',
  '/map',
  '/roles',
  '/tenant-settings',
  '/tenant-communications',
  '/new-analytics',
] as const;

function requiredExplicitWorkingRouteAction(route: string): string | null {
  const pathname = String(route || '').split(/[?#]/, 1)[0] || '/';
  return EXPLICIT_WORKING_ROUTE_ACTIONS[pathname] ?? null;
}

export function getAuthorizedEntryRoute(
  user: { role?: string; access_mode?: 'legacy' | 'explicit'; access_status?: 'pending' | 'active' | 'suspended' } | null | undefined,
  accessPolicy: FrontendAccessPolicy | null | undefined,
  fallbackRoute = '/tickets',
): string {
  if (user?.access_mode !== 'explicit') return isAnalystRole(user?.role) ? KPI_ANALYTICS_ROUTE : fallbackRoute;
  if (user.access_status !== 'active') return '/access-pending';
  if (accessPolicy?.access_mode !== 'explicit') return fallbackRoute;
  const actions = new Set(accessPolicy?.actions ?? []);
  const fallbackAction = requiredExplicitWorkingRouteAction(fallbackRoute);
  if (fallbackAction && actions.has(fallbackAction)) return fallbackRoute;
  return EXPLICIT_WORKING_ROUTE_PRIORITY.find((route) => {
    const requiredAction = requiredExplicitWorkingRouteAction(route);
    return Boolean(requiredAction && actions.has(requiredAction));
  })
    ?? '/access-pending';
}

export function buildFallbackPlatformEntryContext(
  user: User | null | undefined,
  accessPolicy: FrontendAccessPolicy | null | undefined,
  hostname?: string | null,
): PlatformEntryContext {
  if (!user || (user.access_mode === 'explicit' && user.access_status !== 'active')) {
    return { entry_kind: 'pending', contours: [], default_contour: null, can_switch_all: false };
  }

  const tenantIds = Array.from(new Set(
    (accessPolicy?.tenant_scope_ids ?? (user.tenant_id ? [user.tenant_id] : []))
      .map((tenantId) => String(tenantId || '').trim().toLowerCase())
      .filter(Boolean),
  ));
  const contours = tenantIds.map((tenantId) => ({
    tenant_id: tenantId,
    label: tenantId,
    host: `${tenantId}.moy-gorod.su`,
  }));
  // The role is descriptive here, not an authorization source. A root fallback
  // is safe only when the access snapshot explicitly grants the global action.
  const canManageGlobalAccess = Boolean(accessPolicy?.actions?.includes('users.manage_access'));
  if (isPublicPlatformEntryHostname(hostname) && canManageGlobalAccess) {
    return {
      entry_kind: 'root_admin',
      contours,
      default_contour: tenantIds[0] ?? null,
      can_switch_all: true,
    };
  }
  if (isPublicPlatformEntryHostname(hostname) && user.access_mode !== 'explicit') {
    // A legacy city role is not proof of platform ownership. Until the root
    // entry-context endpoint confirms the exact allowlisted owner, stay on the
    // root host with no business-data route instead of falling into its home
    // city during a timeout or transient backend failure.
    return { entry_kind: 'pending', contours: [], default_contour: null, can_switch_all: false };
  }
  return {
    entry_kind: tenantIds.length > 1 ? 'multi_contour' : 'single_contour',
    contours,
    default_contour: tenantIds[0] ?? null,
    can_switch_all: false,
  };
}

export function resolveTransientPlatformEntryContext(
  user: User | null | undefined,
  accessPolicy: FrontendAccessPolicy | null | undefined,
  verifiedEntryContext: PlatformEntryContext | null | undefined,
  hostname?: string | null,
): PlatformEntryContext {
  return verifiedEntryContext ?? buildFallbackPlatformEntryContext(user, accessPolicy, hostname);
}

function appendContourQuery(route: string, tenantId: string | null | undefined): string {
  const normalized = String(tenantId || '').trim().toLowerCase();
  if (!normalized) return route;
  const separator = route.includes('?') ? '&' : '?';
  return `${route}${separator}contour=${encodeURIComponent(normalized)}`;
}

export function getPlatformPostLoginRoute(
  user: User | null | undefined,
  accessPolicy: FrontendAccessPolicy | null | undefined,
  entryContext: PlatformEntryContext | null | undefined,
  fallbackRoute = '/tickets',
  hostname?: string | null,
): string {
  const authorizedRoute = getAuthorizedEntryRoute(user, accessPolicy, fallbackRoute);
  if (authorizedRoute === '/access-pending') return authorizedRoute;
  const context = entryContext ?? buildFallbackPlatformEntryContext(user, accessPolicy, hostname);
  if (context.entry_kind === 'pending') return '/access-pending';
  if (context.current_workspace) {
    const targetHost = context.current_workspace.host.toLowerCase();
    const currentHost = String(hostname || '').toLowerCase();
    const hostMatches = targetHost === currentHost
      || (targetHost === 'moy-gorod.su' && currentHost === 'www.moy-gorod.su');
    // A workspace path is not by itself root-platform authority. Otherwise a
    // stale/transitional control workspace can send a non-root envelope to
    // /platform, whose own guard returns to /, creating an endless replace
    // loop. City/vertical hosts remain bound by their exact workspace host;
    // root paths additionally need the same authority check as the page.
    const rootWorkspaceAllowed = !isPublicPlatformEntryHostname(hostname)
      || canOpenPlatformSurface(context, context.current_workspace.path);
    if (hostMatches && rootWorkspaceAllowed) {
      return context.current_workspace.path;
    }
  }
  if (context.entry_kind === 'root_admin' && isPublicPlatformEntryHostname(hostname)) {
    return '/platform';
  }
  if (context.entry_kind === 'multi_contour') return '/select-contour';
  if ((context.workspaces?.length ?? 0) === 1 && isPublicPlatformEntryHostname(hostname)) {
    return '/select-contour?auto=1';
  }
  return appendContourQuery(authorizedRoute, context.default_contour ?? context.contours[0]?.tenant_id);
}

export function canOpenPlatformSurface(
  entryContext: PlatformEntryContext | null | undefined,
  pathname: string,
): boolean {
  if (entryContext?.entry_kind === 'root_admin') return true;
  return pathname === '/platform/uk'
    && entryContext?.current_workspace?.kind === 'uk_portfolio';
}

export function narrowAccessPolicyToContour(
  accessPolicy: FrontendAccessPolicy | null | undefined,
  tenantId: string | null | undefined,
): FrontendAccessPolicy | null | undefined {
  if (!accessPolicy || accessPolicy.access_mode !== 'explicit') return accessPolicy;
  const normalized = String(tenantId || '').trim().toLowerCase();
  const dimensions = accessPolicy.scope_summary?.[normalized] ?? {};
  const actions = Array.from(new Set((dimensions.action ?? [])
    .map((action) => String(action || '').trim().toLowerCase())
    .filter(Boolean)));
  const actionSet = new Set(actions);
  const canManageAccess = actionSet.has('users.manage_access');
  const canManageMessages = actionSet.has('messages.manage');
  const visibleSections = [
    actionSet.has('messages.read') ? 'messages' : null,
    actionSet.has('tickets.read') ? 'tickets' : null,
    actionSet.has('map.read') ? 'map' : null,
    canManageAccess ? 'roles' : null,
  ].filter((section): section is string => Boolean(section));
  return {
    ...accessPolicy,
    capability_profile: normalized ? 'explicit-contour' : 'explicit-contour-missing',
    operator_above_department_head: false,
    can_access_helpdesk_support: false,
    can_access_helpdesk_engineering: false,
    can_access_helpdesk_system_tasks: false,
    can_reply_in_chat: actionSet.has('messages.reply'),
    can_access_settings_surface: actionSet.has('governance.read'),
    can_access_tenant_settings_page: actionSet.has('governance.read'),
    has_full_ticket_visibility: false,
    can_manage_tenant_users: canManageAccess,
    can_reset_user_passwords: false,
    can_access_admin_surface: false,
    can_access_developer_workbench: false,
    can_access_roles_page: canManageAccess,
    can_access_users_role_surface: canManageAccess,
    can_access_telegram_roles_surface: false,
    can_manage_role_menu_visibility: false,
    can_access_message_feed_advanced_bulk_edit: canManageMessages,
    can_access_message_feed_pipe_cases_filter: canManageMessages,
    can_access_message_feed_cleanup: canManageMessages,
    can_access_message_feed_moderation_labels: canManageMessages,
    can_access_kpi_analytics: actionSet.has('analytics.read'),
    allowed_target_roles: [],
    observer_whitelist_sections: visibleSections,
    tenant_scope_ids: normalized && (accessPolicy.tenant_scope_ids ?? []).includes(normalized)
      ? [normalized]
      : [],
    scope_summary: normalized && accessPolicy.scope_summary?.[normalized]
      ? { [normalized]: accessPolicy.scope_summary[normalized] }
      : {},
    actions,
  };
}

export function scopeAccessPolicyToWorkingContext(
  accessPolicy: FrontendAccessPolicy | null | undefined,
  tenantId: string | null | undefined,
  workspaceKind: string | null | undefined,
): FrontendAccessPolicy | null | undefined {
  const normalizedTenant = String(tenantId || '').trim().toLowerCase();
  if (normalizedTenant) return narrowAccessPolicyToContour(accessPolicy, normalizedTenant);
  if (
    accessPolicy?.access_mode === 'explicit'
    && String(workspaceKind || '').trim().toLowerCase() === 'vertical'
  ) {
    // A vertical is itself the server-authorized working envelope and may
    // intentionally span several tenants. With no selected city, preserve the
    // already workspace-narrowed policy instead of manufacturing an empty
    // contour policy that redirects active users to /access-pending.
    return accessPolicy;
  }
  return narrowAccessPolicyToContour(accessPolicy, null);
}

export function canAccessDeveloperWorkbench(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_developer_workbench', normalized ? DEVELOPER_WORKBENCH_ROLES.has(normalized) : false);
}

export function canAccessTenantSettingsPage(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_tenant_settings_page', normalized ? TENANT_SETTINGS_ROLES.has(normalized) : false);
}

export function canAccessRolesPage(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_roles_page', normalized ? ROLES_PAGE_ROLES.has(normalized) : false);
}

export function canAccessUsersRoleSurface(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_users_role_surface', normalized ? ROLES_PAGE_ROLES.has(normalized) : false);
}

export function canAccessTelegramRolesSurface(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_telegram_roles_surface', normalized ? TELEGRAM_ROLES_ROLES.has(normalized) : false);
}

export function canManageRoleMenuVisibility(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_manage_role_menu_visibility', normalized ? MENU_VISIBILITY_ROLES.has(normalized) : false);
}

export function canAccessAdminSurface(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_admin_surface', normalized ? ADMIN_SURFACE_ROLES.has(normalized) : false);
}

export function canUseMaxPersonalReply(
  role: string | null | undefined,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return normalized ? MAX_PERSONAL_REPLY_ROLES.has(normalized) : false;
}

export function canAccessTicketNotes(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  if (accessPolicy?.access_mode === 'explicit') {
    return Boolean(accessPolicy.actions?.includes('tickets.write'));
  }
  const normalized = normalizeFrontendRole(role);
  return normalized === 'admin' || normalized === 'super_admin';
}

export function canAccessHelpdeskSupport(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_helpdesk_support', normalized ? DEVELOPER_WORKBENCH_ROLES.has(normalized) : false);
}

export function canAccessHelpdeskEngineering(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_helpdesk_engineering', normalized ? DEVELOPER_WORKBENCH_ROLES.has(normalized) : false);
}

export function canAccessHelpdeskSystemTasks(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(
    accessPolicy,
    'can_access_helpdesk_system_tasks',
    normalized ? HELPDESK_SYSTEM_TASKS_CONTROL_ROLES.has(normalized) : false,
  );
}

export function canAccessMessageFeedAdvancedBulkEdit(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_message_feed_advanced_bulk_edit', normalized ? ['admin', 'developer'].includes(normalized) : false);
}

export function canAccessMessageFeedPipeCasesFilter(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_message_feed_pipe_cases_filter', normalized ? ['developer', 'admin', 'chief', 'super_admin'].includes(normalized) : false);
}

export function canAccessMessageFeedCleanup(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_message_feed_cleanup', normalized ? ['developer', 'admin', 'chief', 'super_admin'].includes(normalized) : false);
}

export function canAccessMessageFeedModerationLabels(
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): boolean {
  const normalized = normalizeFrontendRole(role);
  return policyOrFallback(accessPolicy, 'can_access_message_feed_moderation_labels', normalized ? ['developer', 'admin', 'chief', 'super_admin'].includes(normalized) : false);
}

export function projectMenuItemsForRole<T extends { key: string }>(
  menuItems: T[],
  role: string | null | undefined,
  accessPolicy?: FrontendAccessPolicy | null,
): T[] {
  const roleSafeItems = menuItems.filter(
    (item) => item.key !== '/new-analytics' || canAccessKpiAnalytics(role, accessPolicy),
  );
  if (accessPolicy?.access_mode === 'explicit') {
    const actions = new Set(accessPolicy.actions ?? []);
    return roleSafeItems.filter((item) => {
      const requiredAction = requiredExplicitWorkingRouteAction(item.key);
      return requiredAction ? actions.has(requiredAction) : false;
    });
  }
  const whitelistSections = new Set(accessPolicy?.observer_whitelist_sections ?? []);
  const effectiveWhitelist = whitelistSections.size
    ? new Set(Array.from(whitelistSections).map((section) => `/${section}`))
    : OBSERVER_WHITELIST_MENU_KEYS;
  if (isObserverWhitelistRole(role)) {
    return roleSafeItems.filter((item) => effectiveWhitelist.has(item.key));
  }
  if (isAnalystRole(role)) {
    return roleSafeItems.filter((item) => item.key === KPI_ANALYTICS_ROUTE);
  }
  return roleSafeItems;
}
